import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { UserCircle, Mail, Save, Lock, Eye, EyeOff } from 'lucide-react';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const { t, language } = useLanguage();

  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [saving, setSaving] = useState(false);

  // Change password
  const [pwForm, setPwForm] = useState({ current: '', new: '', confirm: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(language === 'el' ? 'Το όνομα είναι υποχρεωτικό' : 'Name is required');
      return;
    }
    setSaving(true);
    try {
      await updateUser(form);
      toast.success(language === 'el' ? 'Το προφίλ ενημερώθηκε!' : 'Profile updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.new) {
      toast.error(language === 'el' ? 'Συμπληρώστε όλα τα πεδία' : 'Fill in all fields');
      return;
    }
    if (pwForm.new.length < 6) {
      toast.error(language === 'el' ? 'Ο νέος κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες' : 'New password must be at least 6 characters');
      return;
    }
    if (pwForm.new !== pwForm.confirm) {
      toast.error(language === 'el' ? 'Οι κωδικοί δεν ταιριάζουν' : 'Passwords do not match');
      return;
    }
    setChangingPw(true);
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current, new_password: pwForm.new });
      toast.success(language === 'el' ? 'Ο κωδικός άλλαξε!' : 'Password changed!');
      setPwForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'el' ? 'Αποτυχία αλλαγής' : 'Change failed'));
    } finally {
      setChangingPw(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: t('admin'),
      senior_facilitator: t('seniorFacilitator'),
      facilitator: t('facilitator'),
    };
    return labels[role] || role;
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in-up" data-testid="profile-page">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#2A1514]">{t('profile')}</h1>
          <p className="text-gray-500 mt-1">
            {language === 'el' ? 'Διαχείριση προσωπικών στοιχείων' : 'Manage your personal information'}
          </p>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-[#E6007E] text-white text-xl">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="font-heading">{user?.name}</CardTitle>
                <Badge className="mt-1">{getRoleLabel(user?.role)}</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <UserCircle size={16} className="text-gray-400" />
                {t('name')}
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={language === 'el' ? 'Ονοματεπώνυμο' : 'Full name'}
                data-testid="profile-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail size={16} className="text-gray-400" />
                {t('email')}
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@example.com"
                data-testid="profile-email-input"
              />
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#E6007E] hover:bg-[#b8005f]"
              data-testid="profile-save-btn"
            >
              <Save size={18} className="mr-2" />
              {saving
                ? (language === 'el' ? 'Αποθήκευση...' : 'Saving...')
                : t('save')
              }
            </Button>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2 text-lg">
              <Lock size={18} className="text-gray-400" />
              {language === 'el' ? 'Αλλαγή Κωδικού' : 'Change Password'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'el' ? 'Τρέχων κωδικός' : 'Current password'}</Label>
              <div className="relative">
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={pwForm.current}
                  onChange={(e) => setPwForm({...pwForm, current: e.target.value})}
                  className="pr-10"
                  data-testid="profile-current-password"
                />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'el' ? 'Νέος κωδικός' : 'New password'}</Label>
              <div className="relative">
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={pwForm.new}
                  onChange={(e) => setPwForm({...pwForm, new: e.target.value})}
                  className="pr-10"
                  data-testid="profile-new-password"
                />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'el' ? 'Επιβεβαίωση νέου κωδικού' : 'Confirm new password'}</Label>
              <Input
                type={showNew ? 'text' : 'password'}
                value={pwForm.confirm}
                onChange={(e) => setPwForm({...pwForm, confirm: e.target.value})}
                data-testid="profile-confirm-password"
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={changingPw}
              variant="outline"
              className="w-full border-[#E6007E] text-[#E6007E] hover:bg-[#E6007E]/5"
              data-testid="profile-change-password-btn"
            >
              <Lock size={16} className="mr-2" />
              {changingPw
                ? (language === 'el' ? 'Αλλαγή...' : 'Changing...')
                : (language === 'el' ? 'Αλλαγή κωδικού' : 'Change password')
              }
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
